import { SyntheticEvent, useEffect, useState } from 'react';
import { colors } from 'commons/src/constants';
import { SensorTypes, ThresholdType } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import { CustomOptions, ThresholdOption } from 'commons/src/models/customThresholdModels';
import { THRESHOLD_SLIDER_RANGES } from '../../features/devicePage/deviceOverviewConstants';

export type UseThresholdSliderProps = {
    thresholdOptions: ThresholdOption[];
    customOptions?: CustomOptions;
    setSelectedValues: (values: number[]) => void;
    selectedValues: number[];
    sensorType: SensorTypes;
};

type UseThresholdSliderReturn = {
    determineColorValue: (thresholdType: ThresholdType) => string;
    isValidNumberInput: (index: number) => boolean;
    normalizeRanges: (range: ThresholdRange[], min: number, max: number) => ThresholdRange[];
    updateInputValue: (event: SyntheticEvent<HTMLInputElement>, index: number) => void;
    sliderConfig: {
        min: number;
        max: number;
        step: number;
    };
    thumbColors: string[];
};
const useThresholdSlider = ({
    thresholdOptions,
    customOptions,
    setSelectedValues,
    selectedValues,
    sensorType,
}: UseThresholdSliderProps): UseThresholdSliderReturn => {
    const rangeOverride = THRESHOLD_SLIDER_RANGES[sensorType];
    const effectiveMin = rangeOverride?.min ?? customOptions?.minSelectableValue ?? 0;
    const effectiveMax = rangeOverride?.max ?? customOptions?.maxSelectableValue ?? 100;

    const [thumbColors, setThumbColors] = useState<string[]>([]);
    const [sliderConfig, setSliderConfig] = useState({
        min: effectiveMin,
        max: effectiveMax,
        step: customOptions?.thresholdDelta || 1,
    });

    useEffect(() => {
        const min = rangeOverride?.min ?? customOptions?.minSelectableValue ?? 0;
        const max = rangeOverride?.max ?? customOptions?.maxSelectableValue ?? 100;
        setSliderConfig({
            min,
            max,
            step: customOptions?.thresholdDelta || 1,
        });
    }, [customOptions, sensorType]);

    const determineColorValue = (thresholdType: ThresholdType): string => {
        return [ThresholdType.LOW_ALERT, ThresholdType.HIGH_ALERT].includes(thresholdType)
            ? colors.graphRed
            : colors.yellowGlow;
    };

    useEffect(() => {
        if (thresholdOptions.length === 0) return;
        const color =
            sensorType === SensorTypes.temp
                ? [colors.graphBlue, colors.graphRed]
                : thresholdOptions?.map(option => determineColorValue(option.type));
        setThumbColors(color);
        setSelectedValues(thresholdOptions.map(option => option.value));
    }, [thresholdOptions, sensorType]);

    const isValidNumberInput = (index: number): boolean => {
        const currentValue = selectedValues[index];
        const previousValue = selectedValues[index - 1];
        const nextValue = selectedValues[index + 1];

        return (
            currentValue !== undefined &&
            (previousValue === undefined || currentValue > previousValue) &&
            (nextValue === undefined || currentValue < nextValue)
        );
    };
    const normalizeRanges = (range: ThresholdRange[], min: number, max: number): ThresholdRange[] => {
        return range?.map(r => ({
            from: r.from !== undefined ? r.from : min,
            to: r.to !== undefined ? r.to : max,
            rating: r.rating,
        }));
    };
    const allowedDecimals =
        sensorType === SensorTypes.temp || (customOptions?.thresholdDelta && customOptions?.thresholdDelta < 1) ? 1 : 0;

    const numberOfDecimals = (numericValue: number, value: string): number =>
        Math.floor(numericValue) === numericValue ? 0 : value.toString().split('.')[1].length || 0;

    const updateInputValue = (event: SyntheticEvent<HTMLInputElement>, index: number): void => {
        const { value } = event.currentTarget;
        const numericValue = Number(value);

        if (value === '') {
            const updatedValues = [...selectedValues];
            updatedValues[index] = 0;
            setSelectedValues(updatedValues);
            return;
        }

        if (Number.isNaN(numericValue)) return;
        if (allowedDecimals > 0 && numberOfDecimals(numericValue, value) > allowedDecimals) return;

        const updatedValues = [...selectedValues];
        updatedValues[index] = allowedDecimals === 0 ? Number(numericValue.toFixed(0)) : numericValue;
        setSelectedValues(updatedValues);
    };

    return {
        determineColorValue,
        isValidNumberInput,
        normalizeRanges,
        updateInputValue,
        sliderConfig,
        thumbColors,
    };
};

export default useThresholdSlider;
